import React, { useEffect } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { deleteStaff, searchStaff } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import AppPagination from '../../components/Pagination/AppPagination';
import { setPagination } from '../../redux/Actions/TableAction';
import DeleteButton from '../../components/Delete/DeleteButton';
import EditButton from '../../components/Edit/EditButton';
import { getSelectedUnit } from '../../storage/Storage';
import { addStaff, updateStaff, deleteStaff as DeleteStaff } from '../../redux/Reducers/RoleAndPermissionReducer/Endpoints';
import { usePermissions } from '../../navigations/Ristrictions';

function Staff() {
    const dispatch = useDispatch();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const { hasEndpointAccess, loading: permissionsLoading } = usePermissions();

    // Permission checks
    const canEditStaff = hasEndpointAccess(updateStaff);
    const canDeleteStaff = hasEndpointAccess(DeleteStaff);
    const canAddStaff = hasEndpointAccess(addStaff);

    console.log({ canEditStaff, canDeleteStaff, canAddStaff });

    useEffect(() => {
        getStaff();
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

    const getStaff = () => {
        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: {
                unitIds : getSelectedUnit()?._id
            }
        };

        HitApi(json, searchStaff).then((res) => {
            console.log("res", res);
            if (res?.message === "Staff retrieved successfully") {
                dispatch(setApiJson(res?.data));
                
                // Update pagination data in Redux
                const paginationData = {
                    total: res?.totalDocs || res?.totalCount || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };
                
                dispatch(setPagination(paginationData));
            }
        }).catch(error => {
            console.error("Error fetching staff:", error);
        });
    };

    // Function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    // Handle staff actions
    const handleEdit = (row) => {
        console.log("Edit staff:", row);
        if (row?._id) {
            window.location.href = `staff/editStaff/${row._id}`;
        } else {
            alert('Unable to edit: Staff ID not found');
        }
    };

    const handleDelete = (row) => {
        console.log("Delete staff:", row);
        const staffName = row.name || 'this staff member';
        
        if (window.confirm(`Are you sure you want to delete ${staffName}?`)) {
            const json = {
                _id: row?._id
            };
            HitApi(json, deleteStaff).then((res) => {
                console.log("Delete response:", res);
                if (res?.success || res?.message?.includes('deleted successfully')) {
                    // Refresh the data after successful deletion
                    getStaff();
                    alert('Staff member deleted successfully');
                } else {
                    alert('Failed to delete staff member');
                }
            }).catch(error => {
                console.error("Error deleting staff:", error);
                alert('Error occurred while deleting staff member');
            });
        }
    };

    // Define status badge renderer - Updated for boolean status
    const renderStatus = (status) => {
        if (status === undefined || status === null) return null;

        const isActive = status === true || status === "true" || status === "1";
        const bgColor = isActive ? "bg-green-100" : "bg-red-100";
        const textColor = isActive ? "text-green-800" : "text-red-800";
        const label = isActive ? "Active" : "Inactive";

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {label}
            </span>
        );
    };

    // Define role badge renderer
    const renderRole = (type) => {
        if (!type) return 'N/A';

        let label = "Employee";
        let bgColor = "bg-blue-100";
        let textColor = "text-blue-800";

        // Handle both string values and full words
        if (type === "A" || type === "Admin") {
            label = "Admin";
            bgColor = "bg-purple-100";
            textColor = "text-purple-800";
        } else if (type === "M" || type === "Manager") {
            label = "Manager";
            bgColor = "bg-yellow-100";
            textColor = "text-yellow-800";
        } else if (type === "E" || type === "Employee") {
            label = "Employee";
            bgColor = "bg-blue-100";
            textColor = "text-blue-800";
        }

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {label}
            </span>
        );
    };

    // Get base index for serial numbers
    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    // Define base table headers without Actions column
    const baseTableHeaders = [
        { 
            title: "S.No", 
            key: "serialNumber", 
            width: "60px", 
            align: "center",
            render: (_, row, index) => {
                // Calculate serial number based on current page and limit
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Name",
            key: "name",
            render: (value, row) => (
                <div className="flex items-center">
                    {row.img ? (
                        <img
                            src={row.img}
                            alt={value}
                            className="w-8 h-8 rounded-full mr-2 object-cover"
                            onError={(e) => {
                                e.target.onerror = null;
                            }}
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                            <span className="text-sm font-medium text-gray-600">
                                {value && value.charAt(0).toUpperCase()}
                            </span>
                        </div>
                    )}
                    <span>{value || 'N/A'}</span>
                </div>
            )
        },
        {
            title: "Staff Code",
            key: "code"
        },
        {
            title: "Email",
            key: "email",
            render: (value) => value || 'N/A'
        },
        {
            title: "Contact",
            key: "contact",
            render: (value) => value || 'N/A'
        },
        {
            title: "Designation",
            key: "designation",
            render: (value) => value || 'N/A'
        },
        {
            title: "Role",
            key: "type",
            render: renderRole
        },
        {
            title: "Status",
            key: "status",
            render: renderStatus
        }
    ];

    // Conditionally add Actions column only if user has edit or delete permissions
    const tableHeaders = [
        ...baseTableHeaders,
        ...(canEditStaff || canDeleteStaff ? [{
            title: "Actions",
            key: "actions",
            align: "center",
            render: (value, row) => (
                <div className="flex justify-center space-x-2">
                    {canEditStaff &&
                        <EditButton 
                            onEdit={() => handleEdit(row)} 
                            itemName="staff" 
                        />
                    }
                    {canDeleteStaff &&
                        <DeleteButton 
                            onDelete={() => handleDelete(row)} 
                            itemName="staff" 
                        />
                    }
                </div>
            )
        }] : [])
    ];

    // Handle row click to view staff details
    const handleRowClick = (row) => {
        console.log("Staff details:", row);
        // Navigate to staff details page
        // window.location.href = `/staff-details/${row._id}`;
    };

    return (
        <div className="p-5">
            <PageHeader
                title={'Staff Management'}
                description={'View and manage staff members'}
            />
            <AppTable
                TH={tableHeaders}
                TD={ApiReducer.apiJson || []}
                onRowClick={handleRowClick}
                isLoading={!ApiReducer.apiJson?.length}
                emptyMessage="No staff members found"
                buttonText={canAddStaff && "Add Staff"}
                navigateTo={canAddStaff && "/addStaff"}
            />
        </div>
    );
}

export default Staff;